import React, { useCallback } from 'react';
import { TabDefinition } from '../../lib/pillarTypes';
import styles from './ConfigurableTabSystem.module.css';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface ConfigurableTabSystemProps {
  tabs: TabDefinition[];
  activeTabIndex: number;
  onTabChange: (index: number) => void;
  /** Optional accent colour for the active tab indicator. */
  accentColor?: string;
  className?: string;
  /** Ref forwarded to the tab-content scroll container. */
  contentRef?: React.RefObject<HTMLDivElement>;
  /** Scroll handler forwarded to the tab-content scroll container. */
  onContentScroll?: () => void;
  /**
   * When true, the tab bar becomes position: sticky and docks to the
   * top of the nearest scrolling ancestor. The content area does NOT
   * get its own overflow-y so the parent scrolls everything as one unit.
   */
  stickyTabBar?: boolean;
  /**
   * When true, the tab bar uses a semi-transparent frosted background
   * instead of solid. Used when the tab bar overlays a hero bleed.
   */
  transparentTabBar?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ConfigurableTabSystem({
  tabs,
  activeTabIndex,
  onTabChange,
  accentColor = 'var(--accent-primary)',
  className,
  contentRef,
  onContentScroll,
  stickyTabBar = false,
  transparentTabBar = false,
}: ConfigurableTabSystemProps) {
  const handleClick = useCallback(
    (index: number, disabled?: boolean) => {
      if (!disabled) onTabChange(index);
    },
    [onTabChange]
  );

  const activeTab = tabs[activeTabIndex];

  return (
    <div className={`${styles.wrapper} ${className ?? ''}`}>
      {/* Tab bar */}
      <div
        className={`${styles.tabBar} ${stickyTabBar ? styles.tabBarSticky : ''} ${transparentTabBar ? styles.tabBarTransparent : ''}`}
        role="tablist"
      >
        {tabs.map((tab, i) => {
          const isActive = i === activeTabIndex;
          const cls = [
            styles.tab,
            isActive ? styles.active : '',
            tab.disabled ? styles.disabled : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              id={`tab-${tab.key}`}
              aria-selected={isActive}
              aria-controls={`panel-${tab.key}`}
              className={cls}
              onClick={() => handleClick(i, tab.disabled)}
              style={
                isActive
                  ? ({ '--tab-accent': accentColor } as React.CSSProperties)
                  : undefined
              }
            >
              {tab.icon && <span className={styles.tabIcon}>{tab.icon}</span>}
              {tab.label}
              {tab.badge != null && (
                <span className={styles.tabBadge}>{tab.badge}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Active tab content */}
      <div
        ref={contentRef as React.RefObject<HTMLDivElement> | undefined}
        className={`${styles.tabContent} ${stickyTabBar ? styles.tabContentFlow : ''}`}
        role="tabpanel"
        id={activeTab ? `panel-${activeTab.key}` : undefined}
        aria-labelledby={activeTab ? `tab-${activeTab.key}` : undefined}
        onScroll={onContentScroll}
      >
        {activeTab?.content}
      </div>
    </div>
  );
}
